import { useEffect, useRef, useState } from "react"
import { useQuery } from "react-query"
import { betterFetch } from "../../utils/apis"
import Checkbox from "components/Checkbox"
import ConditionalView from "components/ConditionalView"
import _keyBy from "lodash/keyBy"
import { useOnClickOutside } from "./useOnClickOutside"
import cn from "classnames"
type chooserPropsType = {
  endpoint: string
  selected: any
  enablePropagation?: boolean
  className?: string
  style?: string | ""
  placeholder?: string
  mapper?: any
  onChange?: any
  keys?: string[]
  labelKey?: string | number // ( labelKey = name | tittle ) for extracting display name
  getLabel?: any
  defaultValue?: any
  floatPriority?: string | number
  shortList?: boolean
  fullWidthDropdown?: boolean
  singleChooser?: boolean
  refreshOnEndPoint?: boolean
  refreshOnEmpty?: boolean
  readOnly?: boolean
  syncSelected?: boolean
  showWarning?: boolean
  showError?: boolean
}

type option = {
  value: number
  label: string
}

const Chooser = (props: chooserPropsType) => {
  const chooserRef = useRef<any>(null)
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [sortedData, setSortedData] = useState<any>([])
  const selectedSingleItem = useRef<any>(null)
  const [selected, setSelected] = useState<any>(
    _keyBy(props.selected, (item: any) => item.id ?? item)
  )

  const { isLoading, refetch, isRefetching } = useQuery(
    props.keys ?? props.endpoint,
    betterFetch(props.endpoint, {
      mapper: props.mapper,
    }),
    { enabled: false }
  )
  useOnClickOutside(chooserRef, () => {
    setOpen(false)
    setSearchText("")
  })

  useEffect(() => {
    if (props.refreshOnEmpty && !props.selected?.length) {
      setSelected(_keyBy(props.selected, (item: any) => item.id ?? item))
    }
  }, [props.selected])

  useEffect(() => {
    if (props.refreshOnEndPoint) {
      setSelected(_keyBy(props.selected, (item: any) => item.id ?? item))
    }
  }, [props.endpoint])

  useEffect(() => {
    if (!props?.syncSelected) return
    if (JSON.stringify(props.selected) !== JSON.stringify(selected)) {
      setSelected(_keyBy(props.selected, (item: any) => item.id ?? item))
    }
  }, [props?.selected, props?.syncSelected])

  const sortData = (arrayData: any) => {
    const selectedItems: any = []
    const unselectedItems: any = []
    const x = arrayData.map((item: any) => {
      if (selected[item.value]) {
        return selectedItems.push(item)
      }
      unselectedItems.push(item)
    })
    return [...selectedItems, ...unselectedItems]
  }

  // const sortData = (arrayData: any) =>
  //   arrayData.sort((item: any) => (selected[item.value] ? -1 : 1))

  const totalSelected = Object.keys(selected).length

  const getTitle = () => {
    if (totalSelected < 1) return props?.placeholder || "Choose..."
    if (totalSelected !== 1) return `${totalSelected} selected`

    const keySingleItem = Object.keys(selected)?.[0]
    const singleWithLabel = selected?.[keySingleItem]

    if (totalSelected === 1 && singleWithLabel?.label) {
      selectedSingleItem.current = {
        value: keySingleItem,
        label: singleWithLabel?.label,
      }
      return (
        singleWithLabel?.label ||
        props.getLabel?.(selected?.[keySingleItem]) ||
        "1 Selected"
      )
    }

    let singleItem = sortedData.find((item: any) => selected[item?.value])

    if (!singleItem) {
      singleItem = {
        value: keySingleItem,
        label:
          props.getLabel?.(selected?.[keySingleItem]) ??
          selected?.[keySingleItem]?.[props.labelKey ?? ""],
      }
    }
    selectedSingleItem.current = singleItem

    return (
      singleItem?.label ||
      props.getLabel?.(selected?.[keySingleItem]) ||
      "1 Selected"
    )
  }

  const label = getTitle()
  // totalSelected > 1
  //   ? `${totalSelected} selected`
  //   : totalSelected > 0
  //   ? data?.[`_${Object.keys(selected)[0]}`]?.label
  //   : props.placeholder ?? 'Choose...'

  const onSingleItemRemove = (e: any) => {
    onClick(selectedSingleItem.current)(e)
    selectedSingleItem.current = null
  }

  const onRefetch = async () => {
    const { data } = await refetch()
    const options = sortData(
      Array.isArray(data) ? data : Object.values(data ?? {})
    )
    setSortedData(options)
  }

  const onOpen = async () => {
    if (!open) onRefetch()
    setOpen(true)
  }

  const onClick = (option: option) => (e: any) => {
    e?.stopPropagation?.()
    if (!!props.readOnly) return
    if (props.singleChooser) {
      const obj = selected[option.value] ? {} : { [option.value]: true }
      props.onChange &&
        props.onChange(obj, selected[option.value] ? {} : option)
      return setSelected(obj)
    }
    let obj = { ...selected }
    // Delete if unselected
    if (obj[option.value]) {
      delete obj[option.value]
    } else {
      obj = {
        ...selected,
        [option.value]: !selected[option.value],
      }
    }
    // makeapicall
    props.onChange && props.onChange(obj, option)
    setSelected({ ...obj })
  }

  const filterItem = (option: option) =>
    !searchText ||
    option.label?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase())

  const onChangeSelectAll = (status: any) => {
    const obj = searchText ? selected : {}
    const filteredItems = sortedData?.filter(filterItem)

    if (Object.keys(selected).length !== sortedData?.length && !searchText) {
      sortedData.forEach((item: any) => (obj[item.value] = true))
    }

    if (searchText && status) {
      filteredItems?.forEach((item: any) => (obj[item.value] = true))
    }

    if (searchText && !status) {
      filteredItems?.forEach((item: any) => delete obj[item.value])
    }

    setSelected({ ...obj })
    props.onChange && props.onChange(obj, filteredItems)
  }

  const getSelectAllStatus = () => {
    if (searchText) {
      return !sortedData
        .filter(filterItem)
        .some((item: any) => !selected[item?.value])
    }
    return Object.keys(selected).length === sortedData?.length
  }

  return (
    <div
      className={cn(
        `flex relative cursor-pointer rounded-md border-[1px] p-2 py-1 bg-primary-bg min-w-[114px] items-center text-gray-600`,
        {
          " border-yellow-500": !!props.showWarning && !props.showError,
          "border-red-500": !!props.showError,
          [props?.style ?? ""]: !!props.style,
        },
        props.className ?? ""
      )}
      ref={chooserRef}
      onClick={(e) => {
        !props.enablePropagation && e.stopPropagation()
        onOpen()
      }}
    >
      <ConditionalView condition={!!props.showWarning || !!props.showError}>
        <i
          className={cn("ri-error-warning-line mr-1", {
            "text-yellow-500": !!props.showWarning && !props.showError,
            "text-red-500": !!props.showError,
          })}
        ></i>
      </ConditionalView>
      <span className="max-w-[18ch] text-ellipsis whitespace-nowrap overflow-hidden mr-auto ml-1">
        {label}
      </span>
      <ConditionalView condition={totalSelected === 1}>
        <>
          <i
            className="ri-close-line mx-2 rounded"
            onClick={onSingleItemRemove}
          />
          <div className="w-[1px] my-[2px] bg-shift-divider h-[20px]" />
        </>
      </ConditionalView>
      <i className="ri-arrow-down-s-line ml-2"></i>
      <ConditionalView condition={open}>
        <div
          style={{ zIndex: props.floatPriority || 10 }}
          className={cn(
            `absolute flex flex-col left-0 top-10 min-w-[220px] max-h-[50vh] overflow-scroll shadow-lg bg-white rounded-lg`,
            {
              "!max-h-[150px]": props.shortList,
              "w-full": props.fullWidthDropdown,
            }
          )}
        >
          <ConditionalView condition={isRefetching}>
            <span className="p-1">Loading</span>
            <div>
              <ConditionalView condition={!isLoading}>
                <div className="flex">
                  <input
                    onClick={(e) => e.stopPropagation()}
                    type="text"
                    value={searchText}
                    className="flex flex-1 m-1 p-1 border border-border bg-primary-bg rounded-md w-[100%]"
                    placeholder="Search for item"
                    onChange={(e) => setSearchText(e.target?.value)}
                  />
                </div>
              </ConditionalView>
              <ConditionalView
                condition={
                  !props.singleChooser && !isLoading && !props.readOnly
                }
              >
                <div
                  onClick={onChangeSelectAll}
                  className="w-full flex items-center justify-start"
                >
                  <Checkbox
                    style="ml-1.5 my-2 bg-pink"
                    checked={getSelectAllStatus()}
                    label=""
                    onChange={onChangeSelectAll}
                  />
                  <label className="!font-normal text-md cursor-pointer ml-1">
                    Select All
                  </label>
                </div>
              </ConditionalView>
              {sortedData
                .filter(filterItem)
                ?.map((option: any, index: number) => {
                  return (
                    <>
                      <div
                        className={cn(
                          `min-h-[32px] hover:bg-primary-bg flex items-center gap-2 px-1`,
                          { "bg-green-200": selected[option.value] }
                        )}
                        key={option.value}
                        onClick={onClick(option)}
                      >
                        <ConditionalView condition={!!props.readOnly}>
                          <span>{option.label}</span>
                          <div className="w-full flex items-start justify-start">
                            <Checkbox
                              checked={!!selected[option.value]}
                              label={""}
                              onChange={onClick(option)}
                            />
                            <label className="!font-normal text-md cursor-pointer ml-1">
                              {option.label}
                            </label>
                          </div>
                        </ConditionalView>
                      </div>
                    </>
                  )
                })}
            </div>
          </ConditionalView>
        </div>
      </ConditionalView>
    </div>
  )
}

export default Chooser
